import { z } from "zod";
import { calculationItemInputSchema } from "./calculation.js";
/**
 * The machine integration surface is deliberately narrower than the
 * provider-neutral workflow envelope. Organization, workflow, request and
 * correlation identity are owned by the registered integration and rebuilt by
 * the API after key authentication.
 */
export const integrationScopeSchema = z.enum([
    "READ_RULES",
    "READ_NCM_CATALOG",
    "CALCULATE",
    "READ_CALCULATION",
    /** Submit and poll tax lookup jobs (NCM × route research at scale). */
    "LOOKUP_JOBS",
]);
export const INTEGRATION_SCOPES = integrationScopeSchema.options;
export const integrationKeyStatusSchema = z.enum(["ACTIVE", "REVOKED", "EXPIRED"]);
export const integrationKeyMetadataSchema = z
    .object({
    integrationId: z.string().uuid(),
    keyId: z.string().uuid(),
    name: z.string().min(1).max(120),
    ownerPrincipalId: z.string().uuid(),
    organizationId: z.string().uuid(),
    environment: z.enum(["dev", "stage", "prod"]),
    scopes: z.array(integrationScopeSchema).min(1),
    workflowIds: z.array(z.string().min(1).max(100)).min(1),
    expiresAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
    revision: z.number().int().positive(),
    status: integrationKeyStatusSchema,
})
    .strict();
export const integrationKeySecretResponseSchema = integrationKeyMetadataSchema
    .extend({
    // An idempotency replay is deliberately metadata-only and omits the
    // one-time secret property altogether.
    secret: z.string().min(1).optional(),
    replayed: z.boolean(),
})
    .strict();
export const integrationKeyWriteSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    ownerPrincipalId: z.string().uuid().optional(),
    organizationId: z.string().uuid().optional(),
    scopes: z.array(integrationScopeSchema).min(1).max(4),
    workflowIds: z.array(z.string().trim().min(1).max(100)).min(1).max(20),
    expiresAt: z.string().datetime().optional(),
    reason: z.string().trim().min(10).max(1_000),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.scopes).size !== value.scopes.length) {
        context.addIssue({ code: "custom", path: ["scopes"], message: "Scopes must be unique" });
    }
    if (new Set(value.workflowIds).size !== value.workflowIds.length) {
        context.addIssue({
            code: "custom",
            path: ["workflowIds"],
            message: "Workflow IDs must be unique",
        });
    }
});
export const integrationKeyRotateSchema = z
    .object({
    overlapHours: z.number().min(0).max(24).default(0),
    expiresAt: z.string().datetime(),
    reason: z.string().trim().min(10).max(1_000),
})
    .strict();
export const integrationKeyRevokeSchema = z
    .object({ reason: z.string().trim().min(10).max(1_000) })
    .strict();
export const integrationBatchCalculationRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    country: z.literal("BR").default("BR"),
    currency: z.literal("BRL").default("BRL"),
    calculationDate: z.string().date(),
    rulesetVersion: z.string().trim().min(1).max(80),
    workflowId: z.string().trim().min(1).max(100),
    batch: z
        .object({ index: z.number().int().nonnegative(), count: z.number().int().positive() })
        .strict(),
    items: z.array(calculationItemInputSchema).min(1).max(500),
})
    .strict()
    .superRefine((request, context) => {
    if (request.items.some((item) => item.monetaryBasis !== undefined || item.supplierType !== undefined))
        context.addIssue({
            code: "custom",
            message: "Explicit monetary basis and supplier profiles require the FISCAL calculation API",
            path: ["items"],
        });
    if (request.batch.index >= request.batch.count) {
        context.addIssue({
            code: "custom",
            message: "batch.index must be less than batch.count",
            path: ["batch", "index"],
        });
    }
    const ids = new Set();
    const positions = new Set();
    for (const item of request.items) {
        if (ids.has(item.itemId)) {
            context.addIssue({ code: "custom", message: "itemId must be unique", path: ["items"] });
        }
        if (positions.has(item.position)) {
            context.addIssue({
                code: "custom",
                message: "position must be unique",
                path: ["items"],
            });
        }
        ids.add(item.itemId);
        positions.add(item.position);
    }
});
export const integrationRulesetMetadataSchema = z
    .object({
    id: z.string().uuid(),
    version: z.string().min(1).max(80),
    status: z.enum(["PUBLISHED", "SUPERSEDED"]),
    contentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    policyVersion: z.string().min(1),
    publishedAt: z.string().datetime(),
})
    .strict();
export const integrationRuleSchema = z
    .object({
    id: z.string().uuid(),
    kind: z.enum(["FISCAL", "OPERATIONAL_COST", "COMMERCIAL_FEE"]),
    ncm: z
        .string()
        .regex(/^\d{8}$/)
        .nullable(),
    cest: z.string().nullable(),
    originUf: z.string().nullable(),
    destinationUf: z.string().nullable(),
    rulesetVersion: z.string().min(1).max(80),
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
    payload: z.record(z.string(), z.unknown()),
    provenance: z.array(z
        .object({
        evidenceId: z.string().uuid(),
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
        source: z.string().min(1),
        sourceUrl: z.string().url().optional(),
        legalReference: z.string().optional(),
        validFrom: z.string().date().nullable(),
        validTo: z.string().date().nullable(),
    })
        .strict()),
    contentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
})
    .strict();
/** Catalog dates are calendar days. node-pg Date values must not ship as `String(date)`. */
export function toIsoDate(value) {
    if (value == null || value === "")
        return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return utcDay(value);
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        const iso = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
        if (iso?.[1])
            return iso[1];
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime()))
            return utcDay(parsed);
    }
    return null;
}
function utcDay(value) {
    return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-${String(value.getUTCDate()).padStart(2, "0")}`;
}
const catalogDateSchema = z.preprocess(toIsoDate, z.string().date().nullable());
export const integrationNcmCatalogItemSchema = z
    .object({
    ncm: z.string().regex(/^\d{8}$/),
    description: z.string().min(1),
    status: z.enum(["active", "inactive"]),
    validFrom: catalogDateSchema,
    validTo: catalogDateSchema,
    legalAct: z.string().nullable(),
    source: z.string().min(1),
    sourceUrl: z.string().url(),
    legalReference: z.string().min(1),
    contentDigest: z.string().min(1),
})
    .strict();
export const integrationNcmCatalogPageSchema = z
    .object({
    items: z.array(integrationNcmCatalogItemSchema).max(100),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const integrationCestCatalogItemSchema = z
    .object({
    cest: z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/),
    segment: z.string().regex(/^\d{2}$/),
    segmentName: z.string().min(1),
    description: z.string().min(1),
    ncmPrefixes: z.array(z.string().min(1)).max(40),
    status: z.enum(["active", "inactive"]),
    legalAct: z.string().nullable(),
    source: z.string().min(1),
    sourceUrl: z.string().url(),
    legalReference: z.string().min(1),
    contentDigest: z.string().min(1),
})
    .strict();
export const integrationCestCatalogPageSchema = z
    .object({
    items: z.array(integrationCestCatalogItemSchema).max(100),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
//# sourceMappingURL=integration.js.map